import { useNavigate } from '@fukuri-admins/hooks/common';
import {
  getToken,
  removeThreeLetterCode,
  removeToken,
  setEmail,
  setThreeLetterCode,
} from '@fukuri-admins/utils/common/cookies';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { getTopLink } from '../../utils/getLinks';

export type UserInfo = {
  partner_admin_user: {
    id: string;
    full_name: string;
    email: string;
  };
};

type AuthContextType = {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  isLoading: boolean;
  signIn: (userInfo: UserInfo, partnerCode: string) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const initializeAuth = async () => {
    try {
      // 初期化時にトークンの存在確認
      const token = getToken();
      if (token) {
        // TODO: ユーザー情報取得APIを呼ぶ
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      removeToken();
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializeAuth();
  }, []);

  const signIn = (user: UserInfo, partnerCode: string) => {
    setIsAuthenticated(true);
    setUserInfo(user);
    setEmail(user.partner_admin_user.email);
    setThreeLetterCode(partnerCode);
    localStorage.setItem('partnerCode', partnerCode);
  };

  const signOut = () => {
    removeToken();
    removeThreeLetterCode();
    setIsAuthenticated(false);
    setUserInfo(null);
    localStorage.removeItem('partnerCode');
    navigate(getTopLink());
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        userInfo,
        isLoading,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
